/* eslint-disable max-lines */
import React, { useMemo } from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@explorer/core';
import {
  Form,
  FormFieldControl,
  ResetButton,
  SubmitButton,
} from '@explorer/forms';
import {
  ControlEscalationPriority,
  ControlUserContactInfoResponse,
} from '@explorer/open-api-client';
import { ContactInfo } from '@explorer/open-api-client/models/ContactInfo';
import AddIcon from '@mui/icons-material/Add';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';

import {
  ContactFormValues,
  initializeFormSchema,
  validationSchema,
} from './formSchema';

export enum ContactModalMode {
  ADD = 'add',
  EDIT = 'edit',
}

interface ContactModalProps {
  mode: ContactModalMode;
  open: boolean;
  onClose: () => void;
  onSave: (contactInfo: ContactInfo) => void;
  isLoading: boolean;
  contact?: ControlUserContactInfoResponse | null;
}

export const ContactModal = ({
  mode,
  open,
  onClose,
  onSave,
  isLoading,
  contact,
}: ContactModalProps) => {
  const intl = useIntl();
  const isEditMode = mode === ContactModalMode.EDIT;
  const formId = isEditMode ? 'EditContactForm' : 'AddContactForm';

  const initialValues: ContactFormValues = {
    email: isEditMode ? contact?.email || '' : '',
    first_name: isEditMode ? contact?.first_name || '' : '',
    last_name: isEditMode ? contact?.last_name || '' : '',
    phone: isEditMode ? contact?.phone || '' : '',
    escalation_priority: isEditMode
      ? contact?.escalation_priority || ControlEscalationPriority.None
      : ControlEscalationPriority.None,
  };

  const formik = useFormik<ContactFormValues>({
    validationSchema: validationSchema(intl),
    initialValues,
    enableReinitialize: isEditMode,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const contactInfo: ContactInfo = {
          email: values.email.trim(),
          first_name: values.first_name.trim() || undefined,
          last_name: values.last_name.trim() || undefined,
          phone: values.phone.trim() || undefined,
          escalation_priority: values.escalation_priority,
        };
        onSave(contactInfo);
        if (!isEditMode) {
          resetForm();
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const formSchema = useMemo(
    () => initializeFormSchema({ formId, formik }),
    [formId, formik],
  );

  const handleClose = () => {
    if (!isEditMode) {
      formik.resetForm();
    }
    onClose();
  };

  const titleKey = isEditMode
    ? 'mdr.escalation-contacts.edit-modal.title'
    : 'mdr.escalation-contacts.modal.add-contact';

  const submitButtonLabel = isEditMode
    ? 'global.update'
    : 'mdr.escalation-contacts.modal.add-contact-button';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{intl.formatMessage({ id: titleKey })}</DialogTitle>
      <DialogContent>
        <Form formId={formId} formik={formik} my={2}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormFieldControl {...formSchema.email} />
            </Grid>
            <Grid item xs={12}>
              <FormFieldControl {...formSchema.escalation_priority} />
            </Grid>
            <Grid item xs={6}>
              <FormFieldControl {...formSchema.first_name} />
            </Grid>
            <Grid item xs={6}>
              <FormFieldControl {...formSchema.last_name} />
            </Grid>
            <Grid item xs={12}>
              <FormFieldControl {...formSchema.phone} />
            </Grid>
          </Grid>
        </Form>
      </DialogContent>
      <DialogActions>
        <ResetButton
          formId={formId}
          formik={formik}
          variant="text"
          label={{ id: 'global.cancel' }}
          onClick={handleClose}
          disabled={isLoading}
        />
        <SubmitButton
          formId={formId}
          formik={formik}
          variant="contained"
          startIcon={!isEditMode ? <AddIcon /> : undefined}
          label={{ id: submitButtonLabel }}
          disabled={isLoading}
          onClick={() => formik.handleSubmit()}
        />
      </DialogActions>
    </Dialog>
  );
};
